// useTheme — Zustand store for the accent and font colors. Persisted to localStorage.

import { create } from "zustand";

interface ThemeState {
  accent: string;
  fg: string;
  fgDim: string;
  setAccent: (themeOrHex: string) => void;
  setFg: (color: string) => void;
  setFgDim: (color: string) => void;
  setThemePair: (fg: string, fgDim: string) => void;
}

const STORAGE_KEY = "shukan.accent";
const STORAGE_FG_KEY = "shukan.fg";
const STORAGE_FG_DIM_KEY = "shukan.fgdim";

const DEFAULT_ACCENT = "#f59e0b";
const DEFAULT_FG = "#e5e5e5";
const DEFAULT_FG_DIM = "#8a8a8a";

const GRADIENT_THEMES: Record<string, { primary: string; secondary: string; css: string }> = {
  "gradient-sunset": {
    primary: "#f59e0b",
    secondary: "#ef4444",
    css: "linear-gradient(135deg, #f59e0b, #ef4444)",
  },
  "gradient-aurora": {
    primary: "#0284c7",
    secondary: "#10b981",
    css: "linear-gradient(135deg, #0284c7, #10b981)",
  },
  "gradient-mint": {
    primary: "#10b981",
    secondary: "#06b6d4",
    css: "linear-gradient(135deg, #10b981, #06b6d4)",
  },
  "gradient-volcano": {
    primary: "#ef4444",
    secondary: "#7f1d1d",
    css: "linear-gradient(135deg, #ef4444, #7f1d1d)",
  },
  "gradient-cyber": {
    primary: "#8b5cf6",
    secondary: "#ec4899",
    css: "linear-gradient(135deg, #8b5cf6, #ec4899)",
  },
  "gradient-forest": {
    primary: "#059669",
    secondary: "#84cc16",
    css: "linear-gradient(135deg, #059669, #84cc16)",
  },
  "gradient-ocean": {
    primary: "#3b82f6",
    secondary: "#22d3ee",
    css: "linear-gradient(135deg, #3b82f6, #22d3ee)",
  },
  "gradient-vaporwave": {
    primary: "#ff007f",
    secondary: "#00f2fe",
    css: "linear-gradient(135deg, #ff007f, #00f2fe)",
  },
  "gradient-solar": {
    primary: "#f97316",
    secondary: "#eab308",
    css: "linear-gradient(135deg, #f97316, #eab308)",
  },
  "gradient-cosmic": {
    primary: "#f43f5e",
    secondary: "#fb7185",
    css: "linear-gradient(135deg, #f43f5e, #fb7185)",
  },
  "gradient-amethyst": {
    primary: "#6366f1",
    secondary: "#d946ef",
    css: "linear-gradient(135deg, #6366f1, #d946ef)",
  },
  "gradient-midnight": {
    primary: "#be123c",
    secondary: "#1e1b4b",
    css: "linear-gradient(135deg, #be123c, #1e1b4b)",
  },
  "gradient-zen": {
    primary: "#4b5563",
    secondary: "#1f2937",
    css: "linear-gradient(135deg, #4b5563, #1f2937)",
  },
};

function loadAccent(): string {
  if (typeof window === "undefined") return DEFAULT_ACCENT;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw && (/^#[0-9a-fA-F]{6}$/.test(raw) || raw.startsWith("gradient-"))) return raw;
  } catch { /* localStorage blocked — fall through */ }
  return DEFAULT_ACCENT;
}

function loadFg(): string {
  if (typeof window === "undefined") return DEFAULT_FG;
  try {
    const raw = window.localStorage.getItem(STORAGE_FG_KEY);
    if (raw && /^#[0-9a-fA-F]{6}$/.test(raw)) return raw;
  } catch { /* localStorage blocked */ }
  return DEFAULT_FG;
}

function loadFgDim(): string {
  if (typeof window === "undefined") return DEFAULT_FG_DIM;
  try {
    const raw = window.localStorage.getItem(STORAGE_FG_DIM_KEY);
    if (raw && /^#[0-9a-fA-F]{6}$/.test(raw)) return raw;
  } catch { /* localStorage blocked */ }
  return DEFAULT_FG_DIM;
}

function applyCss(themeOrHex: string, fg: string, fgDim: string): void {
  if (typeof document === "undefined") return;

  // Apply Font colors
  document.documentElement.style.setProperty("--fg", fg);
  document.documentElement.style.setProperty("--fg-dim", fgDim);

  // Apply Accent
  if (themeOrHex.startsWith("gradient-")) {
    const theme = GRADIENT_THEMES[themeOrHex];
    if (theme) {
      document.documentElement.style.setProperty("--accent", theme.primary);
      document.documentElement.style.setProperty("--accent-gradient", theme.css);
      document.documentElement.style.setProperty("--accent-glow", theme.primary + "59"); // 35% alpha
      return;
    }
  }
  document.documentElement.style.setProperty("--accent", themeOrHex);
  document.documentElement.style.setProperty("--accent-gradient", themeOrHex);
  document.documentElement.style.setProperty("--accent-glow", themeOrHex + "59");
}

export const useTheme = create<ThemeState>((set, get) => ({
  accent: loadAccent(),
  fg: loadFg(),
  fgDim: loadFgDim(),
  setAccent: (themeOrHex) => {
    if (!/^#[0-9a-fA-F]{6}$/.test(themeOrHex) && !themeOrHex.startsWith("gradient-")) return;
    applyCss(themeOrHex, get().fg, get().fgDim);
    try { window.localStorage.setItem(STORAGE_KEY, themeOrHex); } catch { /* swallow */ }
    set({ accent: themeOrHex });
  },
  setFg: (color) => {
    if (!/^#[0-9a-fA-F]{6}$/.test(color)) return;
    applyCss(get().accent, color, get().fgDim);
    try { window.localStorage.setItem(STORAGE_FG_KEY, color); } catch { /* swallow */ }
    set({ fg: color });
  },
  setFgDim: (color) => {
    if (!/^#[0-9a-fA-F]{6}$/.test(color)) return;
    applyCss(get().accent, get().fg, color);
    try { window.localStorage.setItem(STORAGE_FG_DIM_KEY, color); } catch { /* swallow */ }
    set({ fgDim: color });
  },
  setThemePair: (fg, fgDim) => {
    if (!/^#[0-9a-fA-F]{6}$/.test(fg) || !/^#[0-9a-fA-F]{6}$/.test(fgDim)) return;
    applyCss(get().accent, fg, fgDim);
    try {
      window.localStorage.setItem(STORAGE_FG_KEY, fg);
      window.localStorage.setItem(STORAGE_FG_DIM_KEY, fgDim);
    } catch { /* swallow */ }
    set({ fg, fgDim });
  },
}));

// Apply on module load (client-side).
if (typeof window !== "undefined") {
  const state = useTheme.getState();
  applyCss(state.accent, state.fg, state.fgDim);
}
